import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import html2canvas from "html2canvas";
import { Share2, Loader2 } from "lucide-react";
import { getUsers } from "../../utils/getUser";
import { base64ToFile, formatNumberToDollar } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { analyticsMember } from "../../reducers/analytics.slice";
import { getAllOrderDetail } from "../../reducers/order.slice";
import { getAllShipmentDetail } from "../../reducers/shipment.slice";
import { TIER_CONFIG, TIER_ORDER, getTierKey, stringToColor } from "../../components/member/tierConfig";
import { Button } from "../../components/ui/button";
import useMediaQuery from "../../components/shared/useMediaQuery";

// Caps the sample used to derive lifetime totals client-side (no aggregate
// endpoint exists) — generous for a per-member order/shipment history.
const LIFETIME_SAMPLE_LIMIT = 200;

const MONO = "ui-monospace, SF Mono, Menlo, Fira Code, monospace";

function formatTenure(dateStr) {
  if (!dateStr) return "—";
  const start = new Date(dateStr);
  if (Number.isNaN(start.getTime())) return "—";
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 1) return "New";
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}yr`;
}

// Faithful port of the artifact's flip-card (pointer-tracked 3D tilt + foil
// highlight, click/keyboard flip to a foil "RSC" back face). Layout, motion,
// and border/foil treatment match 1:1 — only the data plugged into the two
// front-face stat rows was changed, since the artifact's "RANK"/"№ 0847"
// slots have no real backing field (see the skill's "never fabricate data"
// note, which flags this exact page). Real equivalents are used instead.
function FlipCard({ tier, name, handle, memberNumber, avatarSrc, initials, stats, statusRows, onShare, sharing }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const sceneRef = useRef(null);
  const c3dRef = useRef(null);
  const ryRef = useRef(0);
  const reducedRef = useRef(false);

  useEffect(() => {
    reducedRef.current =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);

  const applyTilt = (tx, ty) => {
    if (!c3dRef.current) return;
    c3dRef.current.style.transform = `rotateY(${ryRef.current + ty}deg) rotateX(${-tx}deg)`;
  };

  const handlePointerMove = (e) => {
    if (reducedRef.current || !sceneRef.current || !c3dRef.current) return;
    const r = sceneRef.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    applyTilt((py - 0.5) * 20, (px - 0.5) * 20);
    c3dRef.current.style.setProperty("--mx", `${px * 100}%`);
    c3dRef.current.style.setProperty("--my", `${py * 100}%`);
  };

  const handlePointerLeave = () => applyTilt(0, 0);

  const handleFlip = () => {
    if (!c3dRef.current) return;
    c3dRef.current.style.transition = "transform .6s cubic-bezier(.2,.7,.25,1)";
    ryRef.current = ryRef.current === 0 ? 180 : 0;
    applyTilt(0, 0);
    setTimeout(() => {
      if (c3dRef.current) c3dRef.current.style.transition = "transform .12s ease-out";
    }, 620);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div
        ref={sceneRef}
        role="button"
        tabIndex={0}
        aria-label="Member card — click to flip"
        onClick={handleFlip}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleFlip();
          }
        }}
        onMouseMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
        style={{ perspective: 1100, width: isMobile ? 260 : 290, height: isMobile ? 364 : 406, cursor: "pointer", maxWidth: "100%" }}
      >
        <div
          ref={c3dRef}
          style={{ position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d", transition: "transform .12s ease-out" }}
        >
          {/* Front */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 16,
              overflow: "hidden",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              boxShadow: "0 26px 60px rgba(0,0,0,.5)",
              border: "3px solid transparent",
              backgroundImage:
                "linear-gradient(155deg, #16283c, #1b3350 48%, #14293e), linear-gradient(130deg, #1f7a70, #7fe9dd 30%, #1f7a70 55%, #7fe9dd 78%, #1f7a70)",
              backgroundOrigin: "border-box",
              backgroundClip: "padding-box, border-box",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                mixBlendMode: "color-dodge",
                opacity: 0.5,
                pointerEvents: "none",
                background:
                  "radial-gradient(farthest-corner circle at var(--mx,50%) var(--my,50%), rgba(255,255,255,.7) 0%, rgba(79,209,197,.35) 24%, rgba(34,211,238,.2) 44%, transparent 66%)",
              }}
            />
            <div style={{ position: "absolute", inset: 0, padding: "15px 16px 12px", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.2em", color: "rgba(236,239,247,.55)" }}>
                  RSC · {memberNumber}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: "0.1em",
                    color: "#06231f",
                    background: `linear-gradient(100deg, ${tier.color}, #9ff0e6 50%, ${tier.color})`,
                    padding: "3px 10px",
                    borderRadius: 999,
                  }}
                >
                  {tier.emoji} {tier.label}
                </span>
              </div>

              <div
                style={{
                  width: 104,
                  height: 104,
                  margin: "8px auto 6px",
                  borderRadius: "50%",
                  padding: 3.5,
                  background: `conic-gradient(from 210deg, ${tier.color}, #00ADB5, #e8b84b, ${tier.color})`,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    background: "#101a2b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {avatarSrc ? (
                    <img src={avatarSrc} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <span style={{ fontSize: 30, fontWeight: 800, color: "#fff" }}>{initials}</span>
                  )}
                </div>
              </div>

              <h3 style={{ margin: 0, textAlign: "center", fontSize: 20, fontWeight: 900, color: "#fff" }}>{name}</h3>
              <div style={{ textAlign: "center", fontFamily: MONO, fontSize: 10.5, color: "rgba(236,239,247,.55)", margin: "2px 0 10px" }}>
                {handle}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {stats.map((s) => (
                  <div
                    key={s.k}
                    style={{ background: "rgba(8,12,26,.5)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, padding: "6px 9px" }}
                  >
                    <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.14em", color: "rgba(236,239,247,.5)" }}>{s.k}</div>
                    <div style={{ fontFamily: MONO, fontSize: 14.5, fontWeight: 700, color: "#fff" }}>{s.v}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: "auto", borderTop: "1px solid rgba(255,255,255,.14)", paddingTop: 7, display: "flex", flexDirection: "column", gap: 3 }}>
                {statusRows.map((row) => (
                  <div key={row.l} style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                    <span style={{ color: "rgba(236,239,247,.45)", fontWeight: 800, letterSpacing: "0.08em", fontSize: 8.5 }}>{row.l}</span>
                    <span style={{ color: "#fff" }}>{row.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Back */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 16,
              overflow: "hidden",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              boxShadow: "0 26px 60px rgba(0,0,0,.5)",
              transform: "rotateY(180deg)",
              background:
                "repeating-linear-gradient(45deg, rgba(0,173,181,.1) 0 14px, transparent 14px 28px), radial-gradient(90% 70% at 50% 45%, #12333a, #0b1a1d 70%)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 52,
                fontWeight: 900,
                color: "transparent",
                backgroundImage: "conic-gradient(from 200deg, #00ADB5, #22d3ee, #e8b84b, #00ADB5)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
              }}
            >
              RSC
            </span>
          </div>
        </div>
      </div>

      <p style={{ textAlign: "center", fontSize: 11, color: "#c3c8cc", letterSpacing: "0.06em", margin: "10px 0 0" }}>
        DRAG TO TILT · CLICK TO FLIP
      </p>
      <div style={{ marginTop: 16 }}>
        <Button onClick={onShare} disabled={sharing}>
          {sharing ? (
            <Loader2 size={15} style={{ marginRight: 8 }} className="tw-animate-spin" />
          ) : (
            <Share2 size={15} style={{ marginRight: 8 }} />
          )}
          {sharing ? "Preparing image…" : "Share my card"}
        </Button>
      </div>
    </div>
  );
}

// html2canvas can't reliably rasterize the interactive card's conic-gradient
// avatar ring or its double-layer gradient-border trick (both are known
// html2canvas gaps), so the shareable image is captured from this separate,
// simplified, always-in-the-DOM-but-offscreen face instead of the real
// FlipCard — same content, flat solid-color styling in place of the two
// effects that don't rasterize reliably. Never shown to the user directly.
const CaptureCardFace = React.forwardRef(function CaptureCardFace(
  { tier, name, handle, memberNumber, avatarSrc, initials, stats, statusRows },
  ref
) {
  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        left: -9999,
        top: 0,
        width: 290,
        height: 406,
        borderRadius: 16,
        overflow: "hidden",
        border: `3px solid ${tier.color}`,
        background: "linear-gradient(155deg, #16283c, #1b3350 48%, #14293e)",
        padding: "15px 16px 12px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.2em", color: "rgba(236,239,247,.55)" }}>
          RSC · {memberNumber}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: "0.1em",
            color: "#06231f",
            background: tier.color,
            padding: "3px 10px",
            borderRadius: 999,
          }}
        >
          {tier.emoji} {tier.label}
        </span>
      </div>

      <div
        style={{
          width: 104,
          height: 104,
          margin: "8px auto 6px",
          borderRadius: "50%",
          padding: 3.5,
          background: `linear-gradient(135deg, ${tier.color}, #00ADB5, #e8b84b)`,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: "#101a2b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt={name} crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 30, fontWeight: 800, color: "#fff" }}>{initials}</span>
          )}
        </div>
      </div>

      <h3 style={{ margin: 0, textAlign: "center", fontSize: 20, fontWeight: 900, color: "#fff" }}>{name}</h3>
      <div style={{ textAlign: "center", fontFamily: MONO, fontSize: 10.5, color: "rgba(236,239,247,.55)", margin: "2px 0 10px" }}>
        {handle}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {stats.map((s) => (
          <div
            key={s.k}
            style={{ background: "rgba(8,12,26,.5)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, padding: "6px 9px" }}
          >
            <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.14em", color: "rgba(236,239,247,.5)" }}>{s.k}</div>
            <div style={{ fontFamily: MONO, fontSize: 14.5, fontWeight: 700, color: "#fff" }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "auto", borderTop: "1px solid rgba(255,255,255,.14)", paddingTop: 7, display: "flex", flexDirection: "column", gap: 3 }}>
        {statusRows.map((row) => (
          <div key={row.l} style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
            <span style={{ color: "rgba(236,239,247,.45)", fontWeight: 800, letterSpacing: "0.08em", fontSize: 8.5 }}>{row.l}</span>
            <span style={{ color: "#fff" }}>{row.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

function TierStepper({ tierKey }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const currentIndex = TIER_ORDER.indexOf(tierKey);
  const current = TIER_CONFIG[tierKey];
  const progressPct = TIER_ORDER.length > 1 ? (currentIndex / (TIER_ORDER.length - 1)) * 100 : 0;

  return (
    <div
      className="member-scroll"
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "visible",
        paddingBottom: isMobile ? 6 : 0,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
          padding: isMobile ? "22px 0 4px" : "22px 6px 4px",
          minWidth: 0,
        }}
      >
      <div
        style={{
          position: "absolute",
          left: isMobile ? "10%" : "7%",
          right: isMobile ? "10%" : "7%",
          top: isMobile ? 38 : 39,
          height: 2,
          background: "rgba(255,255,255,0.07)",
          borderRadius: 2,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: isMobile ? "10%" : "7%",
          width: `${progressPct * (isMobile ? 0.8 : 0.86)}%`,
          top: isMobile ? 38 : 39,
          height: 2,
          background: `linear-gradient(90deg, #00ADB5, ${current.color})`,
          borderRadius: 2,
          transition: "width 0.4s ease",
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", position: "relative", width: "100%" }}>
        {TIER_ORDER.map((key, i) => {
          const t = TIER_CONFIG[key];
          const isCurrent = i === currentIndex;
          const isPast = i < currentIndex;
          return (
            <div
              key={key}
              style={{
                display: "flex",
                flex: "1 1 0",
                minWidth: 0,
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: isMobile ? 32 : 40,
                  height: isMobile ? 32 : 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: isMobile ? 14 : 17,
                  marginBottom: isMobile ? 7 : 10,
                  background: isCurrent || isPast ? `linear-gradient(135deg, ${t.color}, ${t.color}88)` : "#141416",
                  border: isCurrent ? `2px solid ${t.color}` : "2px solid rgba(255,255,255,0.08)",
                  boxShadow: isCurrent ? `0 0 0 5px ${t.color}1f` : "none",
                }}
              >
                {t.emoji}
              </div>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: isMobile ? 9 : 11,
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                    color: isCurrent ? t.color : "rgba(255,255,255,0.4)",
                  }}
                >
                  {t.label}
                </div>
                <div style={{ fontSize: isMobile ? 9 : 10, color: "rgba(255,255,255,0.28)", marginTop: 2 }}>{t.threshold}</div>
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}

function NumberTile({ label, value, accent }) {
  return (
    <div
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
        border: `1px solid ${accent ? `${accent}33` : "rgba(255,255,255,0.07)"}`,
        borderRadius: 12,
        padding: "13px 15px",
        boxShadow: "0 14px 30px -24px rgba(0,0,0,0.6)",
      }}
    >
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)" }}>
        {label}
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, fontFamily: MONO, color: accent || "#fff", marginTop: 5 }}>
        {value}
      </div>
    </div>
  );
}

export default function MyCardPage() {
  const dispatch = useDispatch();
  const { walletSummary, showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [user] = useState(getUsers());
  const [analytics, setAnalytics] = useState(null);
  const [lifetime, setLifetime] = useState(null);
  const [sharing, setSharing] = useState(false);
  const captureRef = useRef(null);

  const tierKey = getTierKey(user);
  const tier = TIER_CONFIG[tierKey] || TIER_CONFIG.member;
  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Member";
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`;
  const memberNumber = user?.id ? String(user.id).replace(/-/g, "").slice(-4).toUpperCase() : "----";
  // Single source of truth for "is Discord linked" — the status row and
  // warning banner used to read the separate is_discord_verified flag,
  // which can disagree with discord_id (e.g. after an unlink), producing
  // a card that simultaneously says "No Discord linked" and "Linked ✓".
  const isDiscord = Boolean(user?.discord_id);
  const handle = isDiscord ? `@${user.discord_id}` : "No Discord linked";

  let avatarSrc = null;
  if (user?.profile_picture) {
    try {
      avatarSrc = URL.createObjectURL(base64ToFile(user.profile_picture, "avatar", "image/*"));
    } catch {
      avatarSrc = null;
    }
  }

  useEffect(() => {
    if (!user?.id) return;

    dispatch(analyticsMember({ userId: user.id })).then((res) => {
      if (res?.payload) setAnalytics(res.payload);
    });

    Promise.all([
      dispatch(getAllOrderDetail({ skip: 0, limit: LIFETIME_SAMPLE_LIMIT, order_status: [], type: [] })),
      dispatch(getAllShipmentDetail({ skip: 0, limit: LIFETIME_SAMPLE_LIMIT, shipment_status: [], shipment_type: [] })),
    ]).then(([ordersRes, shipmentsRes]) => {
      const orders = ordersRes?.payload?.data || [];
      const unitsSold = orders
        .filter((o) => o.status === "APPROVED")
        .reduce(
          (sum, o) => sum + (o.order_items || []).reduce((s, item) => s + (item.quantity || 0), 0),
          0
        );

      const shipments = shipmentsRes?.payload?.data || [];
      const shipmentsCount = shipmentsRes?.payload?.total ?? shipments.length;
      const boxes = shipments.reduce((sum, s) => sum + (s.total_boxes || 0), 0);

      setLifetime({ unitsSold, shipmentsCount, boxes });
    });
  }, [dispatch, user?.id]);

  // Shared between the interactive FlipCard and the offscreen CaptureCardFace
  // so the shared image always matches what's on screen.
  const cardStats = [
    { k: "EARNED", v: analytics ? formatNumberToDollar(analytics.earnings_overview) : "—" },
    { k: "UNITS", v: lifetime ? lifetime.unitsSold : "—" },
    { k: "SHIPMENTS", v: lifetime ? lifetime.shipmentsCount : "—" },
    { k: "MEMBER", v: formatTenure(user?.date_of_joining) },
  ];
  const cardStatusRows = [
    { l: "STATUS", v: user?.status === "ACTIVE" ? "Active ✓" : user?.status || "—" },
    { l: "DISCORD", v: isDiscord ? "Linked ✓" : "Not linked" },
  ];

  const downloadCardImage = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rsc-card-${memberNumber}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleShare = async () => {
    if (!captureRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Could not render card image");

      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
          showToast?.("Card image copied — paste it in Discord!", "success");
          return;
        } catch {
          // Clipboard image write can fail on permissions/browser support —
          // fall through to a direct download instead of just erroring out.
        }
      }
      downloadCardImage(blob);
      showToast?.("Card image downloaded — share it in Discord!", "success");
    } catch (error) {
      console.error("Error generating card image:", error);
      showToast?.("Could not generate card image", "error");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {!isDiscord && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            marginBottom: 36,
            borderRadius: 12,
            background: "rgba(255,193,7,0.08)",
            border: "1px solid rgba(255,193,7,0.18)",
            color: "#ffc107",
            fontSize: 13,
          }}
        >
          Your Discord username is not linked, so some card details may be incomplete.
        </div>
      )}

      <div style={{ marginBottom: isMobile ? 24 : 36 }}>
        <h1 style={{ fontSize: isMobile ? 23 : 28, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>
          My Card
        </h1>
        <p style={{ fontSize: 14, color: "#c3c8cc", margin: "8px 0 0" }}>
          Your seller identity — tier and stats.
        </p>
      </div>

      <div style={{ marginBottom: isMobile ? 24 : 36 }}>
        <TierStepper tierKey={tierKey} />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "stretch" }}>
        <div style={{ flex: isMobile ? "1 1 100%" : "0 0 300px", marginRight: isMobile ? 0 : 32, marginBottom: 32, display: "flex", justifyContent: isMobile ? "center" : "flex-start" }}>
          <FlipCard
            tier={tier}
            name={name}
            handle={handle}
            memberNumber={memberNumber}
            avatarSrc={avatarSrc}
            initials={initials}
            stats={cardStats}
            statusRows={cardStatusRows}
            onShare={handleShare}
            sharing={sharing}
          />
          <CaptureCardFace
            ref={captureRef}
            tier={tier}
            name={name}
            handle={handle}
            memberNumber={memberNumber}
            avatarSrc={avatarSrc}
            initials={initials}
            stats={cardStats}
            statusRows={cardStatusRows}
          />
        </div>

        {/* Your numbers */}
        <div style={{ flex: "1 1 340px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: "rgba(255,255,255,0.4)",
              marginBottom: 12,
            }}
          >
            YOUR NUMBERS
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "130px" : "140px"}, 1fr))`,
              gap: 12,
            }}
          >
            <NumberTile label="Open Orders" value={analytics ? analytics.open_orders : "—"} />
            <NumberTile label="Pending Quotes" value={analytics ? analytics.pending_quotes : "—"} accent="#e8b84b" />
            <NumberTile label="Pending Shipments" value={analytics ? analytics.pending_shipments : "—"} accent="#af58f2" />
            <NumberTile label="Units Sold" value={lifetime ? lifetime.unitsSold : "—"} />
            <NumberTile label="Shipments" value={lifetime ? lifetime.shipmentsCount : "—"} />
            <NumberTile label="Boxes Shipped" value={lifetime ? lifetime.boxes : "—"} />
            <NumberTile
              label="Earnings Overview"
              value={analytics ? formatNumberToDollar(analytics.earnings_overview) : "—"}
              accent="#4caf50"
            />
            <NumberTile
              label="Wallet Balance"
              value={formatNumberToDollar(walletSummary?.current_balance)}
              accent="#00ADB5"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
